/**
 * Live tail for the session log.
 *
 * Polls `.cai/.cache/sessions.jsonl` every POLL_MS, runs new entries through
 * the correction detector, and prints a dim toast whenever a correction
 * signal lands. Runs in the foreground until Ctrl-C.
 *
 * No filesystem watcher dependency on purpose — polling is robust on macOS
 * cloud-synced folders where chokidar can miss events.
 *
 * Tail tracking: we count *number of lines* already processed instead of
 * byte-offset. Byte-offsets break for UTF-8 (em-dash, emoji, German chars)
 * because `readFileSync(path, "utf8").slice(byteOffset)` slices by code points,
 * not bytes — leading to a misaligned read on the next poll.
 */
import { existsSync, readFileSync } from "node:fs";
import { join } from "node:path";
import chalk from "chalk";
import { detectCorrections } from "./corrections.js";
import type { SessionEntry } from "./recorder.js";

const POLL_MS = 2_000;

interface WatchOptions {
  projectRoot: string;
  pollMs?: number;
  /** Called for each new correction (used by tests). */
  onCorrection?: (entry: SessionEntry) => void;
  /** When set, watch loops this many times then exits (used by tests). */
  maxIterations?: number;
}

export async function watchLearnLog(opts: WatchOptions): Promise<void> {
  const path = join(opts.projectRoot, ".cai", ".cache", "sessions.jsonl");
  const pollMs = opts.pollMs ?? POLL_MS;
  let processedLines = countLines(path);
  let iterations = 0;

  console.log(chalk.bold(`\n  cai learn watch — listening for new corrections`));
  console.log(chalk.dim(`  ${path}`));
  console.log(chalk.dim(`  Press Ctrl-C to stop.\n`));

  while (true) {
    if (opts.maxIterations !== undefined && iterations >= opts.maxIterations) break;
    iterations++;

    if (existsSync(path)) {
      const allLines = readLines(path);

      // Detect rotation/truncation: file is shorter than what we've processed
      if (allLines.length < processedLines) {
        processedLines = 0;
      }

      if (allLines.length > processedLines) {
        const newLines = allLines.slice(processedLines);
        const newEntries = parseEntries(newLines);
        const newCorrections = detectCorrections(newEntries);

        for (const c of newCorrections) {
          const time = new Date(c.ts).toISOString().split("T")[1].slice(0, 5);
          console.log(
            `  ${chalk.cyan(time)}  ${chalk.yellow("⚡ correction")}  ${chalk.dim(`[${c.signal}]`)}  ${c.prompt.slice(0, 80)}${c.prompt.length > 80 ? "…" : ""}`
          );
          if (opts.onCorrection) {
            opts.onCorrection({ ts: c.ts, prompt: c.prompt });
          }
        }

        processedLines = allLines.length;
      }
    }

    await new Promise((r) => setTimeout(r, pollMs));
  }
}

function countLines(path: string): number {
  if (!existsSync(path)) return 0;
  try {
    return readFileSync(path, "utf8").split("\n").filter(Boolean).length;
  } catch {
    return 0;
  }
}

function readLines(path: string): string[] {
  try {
    return readFileSync(path, "utf8").split("\n").filter(Boolean);
  } catch {
    return [];
  }
}

function parseEntries(lines: string[]): SessionEntry[] {
  const entries: SessionEntry[] = [];
  for (const line of lines) {
    if (!line) continue;
    try {
      entries.push(JSON.parse(line) as SessionEntry);
    } catch {
      // skip malformed line
    }
  }
  return entries;
}
